/**
 * Living help (ADR-0066) — the one Markdown renderer for help content (shipped sections now,
 * customer notes in Wave 3). `marked` (dev-rules §1) with four small extensions, then DOMPurify.
 *
 * Grammar → HTML (the HelpPanel delegates clicks on `.help-chip` / `.help-screen-link`; keep the
 * class and attribute names in step with it):
 *
 *   `[text]{id}` / `[text]{/route#id}` → `<button type="button" class="help-chip" data-help-ref="<ref>">text</button>`
 *   `{{gap:g-x-001}}`                 → `<span class="help-gap-marker" data-gap-id="g-x-001">Needs SME</span>`
 *   `:::note` / `:::warning` … `:::`   → `<div class="help-admonition help-admonition--note|warning">…</div>`
 *   `[[/route]]`                       → `<a class="help-screen-link" href="/route">/route</a>`
 *
 * Raw HTML in the source is passed through by `marked` and then sanitised: scripts, styles,
 * iframes and every `on*` handler are removed, so help (and later customer notes) cannot run code.
 */
import DOMPurify from 'dompurify';
import { Marked, type TokenizerAndRendererExtension, type Tokens } from 'marked';

import './help-markdown.css';

const CHIP_RE = /^\[([^\]\n]+)\]\{((?:\/[^#}\s]*#)?[a-z][a-z0-9-]*)\}/;
const GAP_RE = /^\{\{gap:([a-z0-9-]+)\}\}/;
const SCREEN_LINK_RE = /^\[\[(\/[^\]\s]+)\]\]/;
const ADMONITION_RE = /^:::(note|warning)[ \t]*\n([\s\S]*?)\n:::[ \t]*(?:\n+|$)/;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Index of the next `opener` where `re` matches (marked inline `start` hook), else undefined. */
function nextMatch(src: string, re: RegExp, opener: string): number | undefined {
  let from = src.indexOf(opener);
  while (from !== -1) {
    if (re.test(src.slice(from))) return from;
    from = src.indexOf(opener, from + 1);
  }
  return undefined;
}

const helpChip: TokenizerAndRendererExtension = {
  name: 'helpChip',
  level: 'inline',
  start: (src) => nextMatch(src, CHIP_RE, '['),
  tokenizer(src) {
    const m = CHIP_RE.exec(src);
    if (!m) return undefined;
    return { type: 'helpChip', raw: m[0], text: m[1] ?? '', ref: m[2] ?? '' };
  },
  renderer(token) {
    const t = token as Tokens.Generic & { text: string; ref: string };
    return `<button type="button" class="help-chip" data-help-ref="${escapeHtml(t.ref)}">${escapeHtml(t.text)}</button>`;
  },
};

const helpGap: TokenizerAndRendererExtension = {
  name: 'helpGap',
  level: 'inline',
  start: (src) => nextMatch(src, GAP_RE, '{{'),
  tokenizer(src) {
    const m = GAP_RE.exec(src);
    if (!m) return undefined;
    return { type: 'helpGap', raw: m[0], gapId: m[1] ?? '' };
  },
  renderer(token) {
    const t = token as Tokens.Generic & { gapId: string };
    return `<span class="help-gap-marker" data-gap-id="${escapeHtml(t.gapId)}">Needs SME</span>`;
  },
};

const helpScreenLink: TokenizerAndRendererExtension = {
  name: 'helpScreenLink',
  level: 'inline',
  start: (src) => nextMatch(src, SCREEN_LINK_RE, '[['),
  tokenizer(src) {
    const m = SCREEN_LINK_RE.exec(src);
    if (!m) return undefined;
    return { type: 'helpScreenLink', raw: m[0], route: m[1] ?? '' };
  },
  renderer(token) {
    const t = token as Tokens.Generic & { route: string };
    const r = escapeHtml(t.route);
    return `<a class="help-screen-link" href="${r}">${r}</a>`;
  },
};

const helpAdmonition: TokenizerAndRendererExtension = {
  name: 'helpAdmonition',
  level: 'block',
  start(src) {
    const m = /^:::(?:note|warning)/m.exec(src);
    return m ? m.index : undefined;
  },
  tokenizer(src) {
    const m = ADMONITION_RE.exec(src);
    if (!m) return undefined;
    const token = {
      type: 'helpAdmonition',
      raw: m[0],
      kind: m[1] ?? 'note',
      tokens: [] as Tokens.Generic[],
    };
    this.lexer.blockTokens(m[2] ?? '', token.tokens);
    return token;
  },
  renderer(token) {
    const t = token as Tokens.Generic & { kind: string; tokens: Tokens.Generic[] };
    return `<div class="help-admonition help-admonition--${t.kind}">${this.parser.parse(t.tokens)}</div>\n`;
  },
};

const md = new Marked(
  { gfm: true, async: false },
  { extensions: [helpAdmonition, helpChip, helpGap, helpScreenLink] },
);

/** Render one help Markdown fragment to sanitised HTML. Synchronous; safe for untrusted input. */
export function renderHelpMarkdown(source: string): string {
  const raw = md.parse(source, { async: false });
  return DOMPurify.sanitize(raw, {
    ADD_ATTR: ['data-help-ref', 'data-gap-id'],
    ADD_TAGS: ['button'],
    FORBID_TAGS: ['script', 'style', 'iframe'],
  });
}
